import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Flex,
  Heading,
  IconButton,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import { useContext, useEffect, useState } from "react";
import { MdDelete, MdEdit } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { fetchController } from "../../utils/FetchController/fetchController";
import { getHostDomain } from "../../utils/utils";
import CreatePoolModal from "./CreatePoolModal";
import { SettingContext } from "../Settings/Reducer/SettingProvider";
import { FeatureUpgradeContent } from "../shared/FeatureUpgradeModal";

const STRATEGY_LABEL = {
  round_robin: "Round Robin",
  priority: "Priority",
  least_busy: "Least Busy",
};
const STRATEGY_COLOR = {
  round_robin: "purple",
  priority: "blue",
  least_busy: "green",
};

export default function CalendarPoolList() {
  const baseUrl = getHostDomain();
  const navigate = useNavigate();
  const [pools, setPools] = useState([]);
  const [memberCounts, setMemberCounts] = useState({});
  const [status, setStatus] = useState("idle");
  const { state } = useContext(SettingContext) || { state: {} };
  const hasFeature = !!(
    state?.credits?.calendar_pools === true ||
    (typeof state?.credits?.calendar_pools === "number" && state.credits.calendar_pools >= 1)
  );
  const { isOpen, onOpen, onClose } = useDisclosure();

  async function fetchPools() {
    setStatus("loading");
    try {
      const res = await fetchController(
        `${baseUrl}/api/v1/calendar_pool?size=50`,
        "GET"
      );
      const items = res?.data?.items || [];
      setPools(items);
      // Fetch member counts in parallel — cheap, runs once on load
      const counts = {};
      await Promise.all(
        items.map(async (p) => {
          try {
            const r = await fetchController(
              `${baseUrl}/api/v1/calendar_pool/${p.id}/members`,
              "GET"
            );
            counts[p.id] = (r?.data || []).length;
          } catch {
            counts[p.id] = 0;
          }
        })
      );
      setMemberCounts(counts);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  useEffect(() => {
    if (hasFeature) fetchPools();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFeature]);

  // All hooks above run unconditionally — early-return is safe below.
  if (!hasFeature) {
    return (
      <Box pt={10}>
        <Card variant="elevated">
          <CardBody>
            <FeatureUpgradeContent
              title="Calendar Pools"
              description="Distribute meeting bookings across multiple agents' calendars with round-robin, priority, or least-busy strategies. Every appointment lands on a different teammate so workload stays even."
              availablePlans={["Turbo", "Ultra", "Agency Starter", "Agency Pro"]}
              variant="inline"
            />
          </CardBody>
        </Card>
      </Box>
    );
  }

  async function handleDelete(pool) {
    if (!window.confirm(`Delete pool "${pool.name}"? This cannot be undone.`)) return;
    try {
      await fetchController(
        `${baseUrl}/api/v1/calendar_pool/${pool.id}`,
        "DELETE"
      );
      fetchPools();
    } catch (e) {
      alert("Delete failed: " + (e?.message || "unknown error"));
    }
  }

  const loading = status === "loading";

  return (
    <VStack align="stretch" spacing={4} h="100%">
      <Flex align="center" justify="space-between">
        <Box>
          <Heading size="md">Calendar Pools</Heading>
          <Text fontSize="sm" color="gray.500" mt={1}>
            Group multiple agents' calendars so bookings rotate across them.
          </Text>
        </Box>
        <Button colorScheme="blue" onClick={onOpen}>
          + New Pool
        </Button>
      </Flex>

      <Card variant="elevated">
        <CardBody p={0}>
          {loading ? (
            <Flex justify="center" py={10}>
              <Spinner />
            </Flex>
          ) : pools.length === 0 ? (
            <Flex direction="column" align="center" justify="center" py={10} gap={2}>
              <Text color="gray.500">No calendar pools yet</Text>
              <Button size="sm" colorScheme="blue" onClick={onOpen}>
                Create your first pool
              </Button>
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Strategy</Th>
                    <Th isNumeric>Members</Th>
                    <Th>Description</Th>
                    <Th w="120px"></Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {pools.map((p) => (
                    <Tr key={p.id} _hover={{ bg: "gray.50" }}>
                      <Td fontWeight="medium">{p.name}</Td>
                      <Td>
                        <Badge colorScheme={STRATEGY_COLOR[p.strategy] || "gray"}>
                          {STRATEGY_LABEL[p.strategy] || p.strategy}
                        </Badge>
                      </Td>
                      <Td isNumeric>{memberCounts[p.id] ?? "—"}</Td>
                      <Td fontSize="sm" color="gray.500" maxW="300px" isTruncated>
                        {p.description || "—"}
                      </Td>
                      <Td>
                        <Flex gap={2} justify="flex-end">
                          <Tooltip label="Edit">
                            <IconButton
                              size="xs"
                              icon={<MdEdit />}
                              aria-label="Edit pool"
                              onClick={() => navigate(`/library/calendar-pools/${p.id}`)}
                            />
                          </Tooltip>
                          <Tooltip label="Delete">
                            <IconButton
                              size="xs"
                              colorScheme="red"
                              variant="outline"
                              icon={<MdDelete />}
                              aria-label="Delete pool"
                              onClick={() => handleDelete(p)}
                            />
                          </Tooltip>
                        </Flex>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          )}
        </CardBody>
      </Card>

      <CreatePoolModal
        isOpen={isOpen}
        onClose={onClose}
        onCreated={(newPool) => {
          onClose();
          navigate(`/library/calendar-pools/${newPool.id}`);
        }}
      />
    </VStack>
  );
}
